import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SERVICE_NUMS = ["s01", "s02", "s03", "s04", "s05", "s06", "s07"] as const;
const LOT_NUMS = ["lot1", "lot2", "lot3", "lot4", "lot5"] as const;

function FadeSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function ServicesContent() {
  const { t } = useTranslation();

  return (
    <main className="pt-16">
      {/* Page Hero */}
      <section className="bg-navy py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeSection>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("services.hero.label")}
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
              {t("services.hero.title")}
            </h1>
          </FadeSection>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeSection>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("services.core.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-12 leading-snug max-w-xl">
              {t("services.core.title")}
            </h2>
          </FadeSection>

          <div className="space-y-0">
            {SERVICE_NUMS.map((num, i) => (
              <FadeSection key={num} delay={i * 60}>
                <div className="group flex flex-col md:flex-row md:items-start gap-6 py-10 border-b border-cool-gray-200 last:border-0">
                  <span className="text-5xl font-bold text-cool-gray-100 group-hover:text-gold/20 transition-colors duration-300 leading-none w-20 flex-shrink-0 select-none pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-navy mb-3">{t(`services.${num}.title`)}</h3>
                    <p className="text-[15px] text-navy/60 leading-relaxed max-w-2xl">{t(`services.${num}.desc`)}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* Thematic Lots */}
      <section className="bg-cool-gray-50 py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeSection>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("services.lots.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 leading-snug">
              {t("services.lots.title")}
            </h2>
            <p className="text-navy/60 text-[15px] leading-relaxed max-w-2xl mb-12">
              {t("services.lots.p")}
            </p>
          </FadeSection>

          <div className="space-y-0">
            {LOT_NUMS.map((lot, i) => (
              <FadeSection key={lot} delay={i * 80}>
                <div className="flex flex-col md:flex-row md:items-center gap-6 py-7 border-b border-cool-gray-200 last:border-0">
                  <div className="flex-1">
                    <strong className="text-navy text-base font-semibold">{t(`services.${lot}.title`)}</strong>
                    <p className="text-sm text-navy/55 mt-1 leading-relaxed">{t(`services.${lot}.desc`)}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <FadeSection>
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("services.cta.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 max-w-xl mx-auto leading-snug">
              {t("services.cta.title")}
            </h2>
            <p className="text-white/60 text-[15px] leading-relaxed max-w-lg mx-auto mb-10">
              {t("services.cta.p")}
            </p>
            <Link
              to="/partner-registration"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-white font-semibold text-sm rounded-md hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t("services.cta.btn")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </Link>
          </FadeSection>
        </div>
      </section>
    </main>
  );
}
